import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { apiErrorContent } from '@/components/atoms/ApiErrorMessage';
import {
    ToastError,
    ToastInfo,
    type ToastOptions,
    ToastSuccess,
} from '@/components/molecules/ToastTemplate/ToastTemplate';
import { usePermissions } from '@/contexts/auth';
import { type ClientWithLocationsInfo, useGetClientWithLocations } from '@/domain/client';
import {
    type ManagedUser,
    useDeleteUser,
    useGetAllUsers,
    useGetClientUsers,
    useGetInvitableClients,
    useImpersonateUser,
    useInviteUser,
} from '@/domain/users';

import type { UserInviteFormState } from './Components/UserInviteForm';

export interface UserPageData {
    /** The client's users, or every user on the all-users page. */
    userData: ManagedUser[] | undefined;
    isFetching: boolean;
    /** The list failed to load and nothing is on screen from before. */
    loadFailed: boolean;
    client: ClientWithLocationsInfo | undefined;
    /** The clients an OptoScale admin may invite to; undefined for anyone else. */
    clientList: Record<string, unknown>[] | undefined;
    refetchUsers: () => void;
    /** These report a failure in a toast rather than rejecting, as the row buttons move on. */
    deleteUser: (userId: number) => Promise<void>;
    impersonateUser: (userId: number) => Promise<void>;
    /** Toasts the outcome and rejects on failure, so the invite form stays open. */
    inviteUser: (inputs: UserInviteFormState) => Promise<void>;
}

const TOAST_OPTIONS: ToastOptions = {
    position: 'top-right',
    autoClose: 5000,
    closeOnClick: true,
    hideProgressBar: false,
    pauseOnHover: true,
};

/** The user page's data: the users of the client (or all users) and the actions on them. */
export function useUserPageData(clientId: number | undefined): UserPageData {
    const { isOptoScaleAdmin } = usePermissions();
    const { t } = useTranslation();

    const clientUsers = useGetClientUsers(clientId || 0, { enabled: !!clientId });
    const { data: client } = useGetClientWithLocations(clientId || 0, { enabled: !!clientId });
    const allUsers = useGetAllUsers({ enabled: !clientId && isOptoScaleAdmin });
    const { data: clientList } = useGetInvitableClients({ enabled: isOptoScaleAdmin });

    // Read at call time, so an action that settles after the client changed refetches the
    // list on screen rather than the one it started from.
    const clientIdRef = useRef(clientId);
    clientIdRef.current = clientId;
    const refetchUsers = () => {
        if (clientIdRef.current) clientUsers.refetch();
        else allUsers.refetch();
    };

    const { mutateAsync: invite } = useInviteUser();
    const { mutateAsync: remove } = useDeleteUser({ onSettled: refetchUsers });
    const { mutateAsync: impersonate } = useImpersonateUser(() => {
        window.location.replace('/');
    });

    // Runs inside the delete confirmation, which shows a failure, so it rejects.
    const deleteUser = async (userId: number): Promise<void> => {
        await remove(userId);
    };

    const impersonateUser = async (userId: number): Promise<void> => {
        try {
            await impersonate(userId);
        } catch (error: unknown) {
            ToastError(
                apiErrorContent(error, t('userAccess.loginAsUserFailed')),
                'user-impersonate-error'
            );
        }
    };

    const inviteUser = async (inputs: UserInviteFormState): Promise<void> => {
        try {
            await invite(inputs);
        } catch (error) {
            ToastInfo({
                content: apiErrorContent(error, t('common.labels.somethingHappened')),
                status: 'error',
                icon: 'noicon',
                options: { toastId: 'user-invite-error-toast', ...TOAST_OPTIONS },
            });
            throw error;
        }
        ToastSuccess(
            t('userAccess.common.userInvited', { user: inputs.email }),
            'user-invite-success-toast'
        );
        refetchUsers();
    };

    const users = clientId ? clientUsers : allUsers;

    return {
        userData: users.data,
        isFetching: users.isFetching,
        loadFailed: users.error != null && users.data === undefined,
        client,
        clientList,
        refetchUsers,
        deleteUser,
        impersonateUser,
        inviteUser,
    };
}
